import { useEffect, useState } from "react";
import { CheckCircle2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtEuro as euro, fmtDay } from "@/lib/format";
import { Btn, Pill } from "@/components/rp";
import { SignaturePad } from "@/components/SignaturePad";
import { Section } from "@/components/commesse";

const SalBox = ({ s, oid, setL }) => {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [no, setNo] = useState(false);
  const [sig, setSig] = useState(null);
  const send = async (approve) => { try { setL((await api.post(`/client/orders/${oid}/sal/${s.id}`, { approve, name, note, signature: approve ? sig : null })).data); toast.success(approve ? "SAL approvato e firmato, grazie!" : "Abbiamo ricevuto la tua segnalazione"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-2xl bg-white border border-[#C8BDAB] p-4 space-y-3" data-testid={`client-sal-${s.id}`}>
      <p className="font-semibold">{s.label} · {euro(s.amount)} IVA inclusa{s.percent ? ` · lavori al ${s.percent}%` : ""}</p>
      {s.description && <p className="text-sm text-[#757575]">{s.description}</p>}
      <input value={name} onChange={(e) => setName(e.target.value)} className="rp-field" placeholder="Nome e cognome per approvare" data-testid={`client-sal-name-${s.id}`} />
      {!no && <SignaturePad onChange={setSig} testid={`client-sal-signature-${s.id}`} />}
      {no && <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rp-field" placeholder="Cosa non ti torna?" data-testid={`client-sal-note-${s.id}`} />}
      <div className="flex flex-wrap gap-2">
        <Btn disabled={name.trim().length < 3 || !sig} onClick={() => send(true)} data-testid={`client-sal-approve-${s.id}`}><CheckCircle2 className="h-4 w-4" /> Firmo e approvo il SAL</Btn>
        {no ? <Btn variant="outline" onClick={() => send(false)} data-testid={`client-sal-reject-${s.id}`}>Invia segnalazione</Btn> : <Btn variant="outline" onClick={() => setNo(true)} data-testid={`client-sal-question-${s.id}`}>Ho un dubbio</Btn>}
      </div>
    </div>
  );
};

const SceltaBox = ({ s, oid, setL }) => {
  const [r, setR] = useState(s.risposta || "");
  const send = async () => { try { setL((await api.post(`/client/orders/${oid}/scelte/${s.id}`, { risposta: r })).data); toast.success("Scelta inviata"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-2xl bg-white border border-[#EBEBEB] p-4 space-y-2" data-testid={`client-scelta-${s.id}`}>
      <div className="flex items-center gap-2"><p className="font-medium flex-1">{s.titolo}</p><Pill tone={s.stato === "scelto" ? "success" : s.overdue ? "error" : "warning"}>{s.stato === "scelto" ? "Inviata" : `Entro ${fmtDay(s.scadenza)}`}</Pill></div>
      {s.descrizione && <p className="text-sm text-[#757575]">{s.descrizione}</p>}
      <div className="flex gap-2">
        <input value={r} onChange={(e) => setR(e.target.value)} className="rp-field" placeholder="La tua scelta (modello, colore, codice…)" data-testid={`client-scelta-input-${s.id}`} />
        <Btn disabled={!r.trim()} onClick={send} data-testid={`client-scelta-send-${s.id}`}>{s.stato === "scelto" ? "Aggiorna" : "Invia"}</Btn>
      </div>
    </div>
  );
};

export const ClientLavori = ({ oid }) => {
  const [l, setL] = useState(null);
  useEffect(() => { api.get(`/client/orders/${oid}/lavori`).then((r) => setL(r.data)).catch(() => {}); }, [oid]);
  if (!l) return null;
  const pending = l.sal.filter((s) => s.status === "inviato");
  return (
    <>
      {pending.length > 0 && <Section label="SAL da approvare" className="bg-[#E6DDCE]/40 border-[#C8BDAB]" testid="client-sal-section">{pending.map((s) => <SalBox key={s.id} s={s} oid={oid} setL={setL} />)}</Section>}
      {l.scelte.length > 0 && <Section label="Le tue scelte" testid="client-scelte-section"><div className="space-y-2">{l.scelte.map((s) => <SceltaBox key={s.id} s={s} oid={oid} setL={setL} />)}</div></Section>}
      {l.crono.length > 0 && (
        <Section label="Cronoprogramma dei lavori" testid="client-crono-section">
          {l.crono.map((c) => <p key={c.id} className="text-sm py-1.5 border-b border-[#F1EDE6] last:border-0"><b>{fmtDay(c.start)} → {fmtDay(c.end)}</b> · {c.lavorazione}</p>)}
        </Section>
      )}
      {l.materiali.length > 0 && (
        <Section label="Materiali e consegne" testid="client-materiali-section">
          {l.materiali.map((m) => (
            <div key={m.id} className="flex items-center gap-3 text-sm py-1.5"><Truck className="h-4 w-4 text-[#757575]" /><span className="flex-1">{m.descrizione}{m.consegna_prevista && m.stato !== "consegnato" && <span className="text-[#757575]"> · prevista {fmtDay(m.consegna_prevista)}</span>}</span><Pill tone={m.stato === "consegnato" ? "success" : "sand"}>{m.stato_label}</Pill></div>
          ))}
        </Section>
      )}
      {l.sal.some((s) => s.status !== "inviato") && <p className="text-xs text-[#757575]">SAL approvati: {l.sal.filter((s) => s.status === "approvato").map((s) => <span key={s.id}>{s.label} {euro(s.amount)}{s.doc_id && <> (<a href={fileUrl(`/api/orders/${oid}/documents/${s.doc_id}/file`)} target="_blank" rel="noreferrer" className="underline" data-testid={`client-sal-pdf-${s.id}`}>PDF firmato</a>)</>} · </span>)}</p>}
    </>
  );
};
